import { getAccessToken } from '../utils/storage';
import { authFetch, getApiBaseUrl } from '../utils/api';
import { friendlyApiError } from '../utils/apiErrors';

/**
 * Get authorization headers with access token
 * Uses Bearer token from signup/login stored in session storage
 */
const getAuthHeaders = (): Record<string, string> => {
  const accessToken = getAccessToken();

  if (accessToken) {
    return { Authorization: `Bearer ${accessToken}` };
  }

  return {};
};

export type DbType = 'mysql' | 'mssql' | 'postgresql';

export interface DatabaseCredentials {
  connection_name?: string;
  db_type?: DbType;
  db_host: string;
  db_port: number;
  db_name: string;
  db_user: string;
  db_password: string;
}

export interface DatabaseConfiguration extends DatabaseCredentials {
  schema_file?: File;
}

export interface TestCredentialsResponse {
  success: boolean;
  message: string;
}

export interface SaveConfigurationResponse {
  id: number;
  user_id: number;
  connection_name: string;
  db_host: string;
  db_port: number;
  db_name: string;
  db_user: string;
  created_at: string;
  updated_at: string;
}

export interface LoginCredentials {
  tenant_id: string;
  api_key: string;
}

export interface LoginResponse {
  success: boolean;
  tenant_id?: string;
  message: string;
}

export interface LLMInstructionsPayload {
  connection_id: number;
  instructions: string;
}

export interface LLMInstructionsResponse {
  success: boolean;
  message: string;
  id?: string;
}

export interface SchemaUploadResponse {
  success: boolean;
  tenant_id: string;
  filename?: string;
  table_count?: number;
  message: string;
}

export interface SchemaGenerateResponse {
  success: boolean;
  tenant_id: string;
  table_count?: number;
  id?: string;
}

export interface ConnectionDetailsResponse {
  tenant_id: string;
  db_host: string;
  db_port: number;
  db_name: string;
  db_user: string;
  success: boolean;
  message: string;
}

export interface DomainKnowledgeResponse {
  success: boolean;
  user_id: string;
  instruction_set: {
    version: string;
    sql_rules: {
      allowed_operations: string[];
      forbidden_operations: string[];
      no_select_star: boolean;
      default_limit: number;
      avoid_sensitive_columns: string[];
    };
    join_rules: {
      use_foreign_keys_for_joins: boolean;
      join_type_preference: string;
      max_join_depth: number;
    };
    column_roles: {
      metric_patterns: string[];
      timestamp_patterns: string[];
      category_patterns: string[];
      identifier_patterns: string[];
    };
    query_structure_rules: {
      always_use_aliases: boolean;
      group_by_required_for_aggregates: boolean;
      limit_large_results: boolean;
    };
    nl_query_rules: {
      aggregation_keywords: string[];
      trend_keywords: string[];
      comparison_keywords: string[];
      list_keywords: string[];
    };
  };
}

export interface SchemaFile {
  id: string;
  filename: string;
  uploadDate: string;
  fileType: 'sql' | 'json';
  size: number;
}

export const testDatabaseCredentials = async (credentials: DatabaseCredentials): Promise<TestCredentialsResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/client-connections/test-credentials`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    throw new Error(`Failed to test database credentials: ${response.status} ${response.statusText}`);
  }

  const data: TestCredentialsResponse = await response.json();
  return data;
};

export const saveDatabaseConfiguration = async (configuration: DatabaseConfiguration): Promise<SaveConfigurationResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  // Prepare JSON payload (schema_file is excluded from JSON submission)
  const payload: any = {
    db_host: configuration.db_host,
    db_port: configuration.db_port,
    db_name: configuration.db_name,
    db_user: configuration.db_user,
    db_password: configuration.db_password,
  };

  if (configuration.connection_name) {
    payload.connection_name = configuration.connection_name;
  }

  if (configuration.db_type) {
    payload.db_type = configuration.db_type;
  }

  const response = await authFetch(`${apiUrl}/v1/client-connections/user-connections`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    // Extract detailed error message from response body
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = friendlyApiError(response.status, errorData.detail, 'The database configuration');
    throw new Error(errorMessage);
  }

  const data: SaveConfigurationResponse = await response.json();
  return data;
};

export const uploadSchemaFile = async (tenantId: string, file: File): Promise<SchemaUploadResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const formData = new FormData();
  formData.append('schema_file', file);

  const response = await authFetch(`${apiUrl}/v1/client-connections/${tenantId}/schema/upload`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The schema file'));
  }

  const data: SchemaUploadResponse = await response.json();
  return data;
};

export const generateSchema = async (tenantId: string): Promise<SchemaGenerateResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/client-connections/${tenantId}/schema/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The schema'));
  }

  const data: SchemaGenerateResponse = await response.json();
  return data;
};

export const saveLLMInstructions = async (connectionReference: string, instructions: string): Promise<LLMInstructionsResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const payload: LLMInstructionsPayload = {
    connection_id: parseInt(connectionReference),
    instructions
  };

  const response = await authFetch(`${apiUrl}/v1/instruction-sets/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to save LLM instructions: ${response.status} ${response.statusText}`);
  }

  const data: LLMInstructionsResponse = await response.json();
  return data;
};

export const getSchemaFiles = async (): Promise<SchemaFile[]> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/schema/files`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get schema files: ${response.status} ${response.statusText}`);
  }

  const data: SchemaFile[] = await response.json();
  return data;
};

export const deleteSchemaFile = async (fileId: string): Promise<{ success: boolean; message: string }> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/schema/files/${fileId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete schema file: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

export const fetchConnectionDetails = async (tenantId: string): Promise<ConnectionDetailsResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/client-connections/${tenantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch connection details: ${response.status} ${response.statusText}`);
  }

  const data: ConnectionDetailsResponse = await response.json();
  return data;
};

export const fetchUserConnections = async (): Promise<any> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/client-connections/user-connections`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user connections: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

export const generateDomainKnowledge = async (tenantId: string): Promise<DomainKnowledgeResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const payload = {
    connection_id: parseInt(tenantId),
    include_neo4j_mapping: true,
    regenerate_schema: true
  };

  const response = await authFetch(`${apiUrl}/v1/domain-knowledge/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to generate domain knowledge: ${response.status} ${response.statusText}`);
  }

  const data: DomainKnowledgeResponse = await response.json();
  return data;
};

/**
 * Login for existing users using tenant_id and api_key.
 */
export const loginUser = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/client-connections/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'Login'));
  }

  const data: LoginResponse = await response.json();
  return data;
};
export const updateConnectionStatus = async (connectionId: string, isActive: boolean): Promise<any> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/client-connections/user-connections/${connectionId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ is_active: isActive }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update connection status: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

/**
 * Mark a connection as the most recently active one (updates last_active_at on server).
 * Called when switching connections so login can restore the correct connection.
 */
export const setConnectionActive = async (connectionId: string): Promise<any> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/client-connections/user-connections/${connectionId}/set-active`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to set connection as active: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

export interface UpdateConnectionPayload {
  connection_name?: string;
  db_host?: string;
  db_port?: number;
  db_name?: string;
  db_user?: string;
  db_password?: string;
  db_type?: DbType;
  is_active?: boolean;
}

export const updateConnection = async (
  connectionId: string,
  payload: UpdateConnectionPayload
): Promise<any> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/client-connections/user-connections/${connectionId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The connection'));
  }

  const data = await response.json();
  return data;
};

export const deleteConnection = async (connectionId: string): Promise<{ success: boolean; message: string }> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/client-connections/user-connections/${connectionId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete connection: ${response.status} ${response.statusText}`);
  }

  // Backend returns 204 No Content on successful delete — no body to parse
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return { success: true, message: 'Connection deleted successfully' };
  }

  const data = await response.json();
  return data;
};

// ==========================================
// Embedding Status API & WebSocket Functions
// ==========================================

export interface EmbeddingTask {
  id: number;
  user_id: number;
  connection_id: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'ERROR' | 'CANCELLED';
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmbeddingStatusMessage {
  type: string;
  connection_id: number;
  status: string;
  task_id?: number;
  message?: string;
  error?: string;
}

/**
 * Fetch the current embedding status for a specific connection.
 */
export const fetchEmbeddingStatus = async (connectionId: number): Promise<EmbeddingTask | null> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/embedding-status/${connectionId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch embedding status: ${response.status}`);
  }

  const data = await response.json();
  // If no task found, return null
  if (data.status === null) return null;
  return data as EmbeddingTask;
};

/**
 * Fetch all embedding tasks for the authenticated user (latest per connection).
 */
export const fetchUserEmbeddingTasks = async (): Promise<EmbeddingTask[]> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/embedding-status/user-tasks`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch embedding tasks: ${response.status}`);
  }

  const data = await response.json();
  return data.tasks || [];
};

/**
 * Retry a failed embedding task for a specific connection.
 */
export const retryEmbedding = async (connectionId: number): Promise<{ message: string; task: EmbeddingTask }> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/embedding-status/${connectionId}/retry`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to retry embedding: ${response.status}`);
  }

  return response.json();
};

/**
 * Connect to embedding status WebSocket for real-time updates.
 *
 * @param connectionId - Database connection ID to monitor
 * @param onMessage - Callback for status updates
 * @param onError - Optional error callback
 * @param onClose - Optional close callback
 * @returns WebSocket instance (call .close() to disconnect)
 */
export const connectEmbeddingWebSocket = (
  connectionId: number,
  onMessage: (data: EmbeddingStatusMessage) => void,
  onError?: (error: Event) => void,
  onClose?: () => void
): WebSocket => {
  const apiUrl = getApiBaseUrl();
  const wsUrl = apiUrl.replace('http://', 'ws://').replace('https://', 'wss://');

  const ws = new WebSocket(`${wsUrl}/v1/embedding-status/ws/${connectionId}`);

  ws.onopen = () => {
    console.log('[WebSocket] Connected to embedding status:', connectionId);
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage(data);
    } catch (error) {
      console.error('[WebSocket] Failed to parse embedding status message:', error);
    }
  };

  ws.onerror = (error) => {
    console.error('[WebSocket] Embedding status error:', error);
    if (onError) onError(error);
  };

  ws.onclose = () => {
    console.log('[WebSocket] Embedding status connection closed');
    if (onClose) onClose();
  };

  return ws;
};

// Batch Query Types
export interface BatchQueryRequest {
  questions: string[];
  connection_id: number;
  collection_id: string;
  session_id?: string;
  process_id?: string;       // Optional parent process ID for follow-up questions
  widget_id?: string;        // Optional widget ID for widget follow-up questions
  report_context?: Record<string, any>;
  ask_more?: boolean;        // True when query originates from Ask More
  page_session_key?: string; // UUID generated on page load; resets on refresh
  page_size?: number;        // Rows per page (default 10)
}

export interface PaginationMeta {
  process_id: string;
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface PaginateRequest {
  page_session_key: string;
  process_id: string;
  page: number;
  page_size: number;
}

export interface PaginateResponse {
  data: Record<string, any>[];
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  process_id: string;
}

export interface BatchQueryResponse {
  batch_id: string;
  session_id: string;
  total_queries: number;
}

export interface QueryProcessStatus {
  process_id: string;
  query_index: number;
  question: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'ERROR' | 'CANCELLED';
  result?: {
    query: string;
    data: any[];
    visualization: string;
    summary: string;
    result_count: number;
  };
  error?: string;
}

export interface CancelBatchResponse {
  message: string;
}

/**
 * Submit a batch of queries for parallel processing
 * Uses the new /generate-batch endpoint
 */
export const submitBatchQuery = async (request: BatchQueryRequest): Promise<BatchQueryResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/query-generator/generate-batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'Your question'));
  }

  const data: BatchQueryResponse = await response.json();
  return data;
};

/**
 * Fetch a specific page of an existing query result without calling the LLM.
 * The original query must have been submitted with a page_session_key.
 */
export const paginateQuery = async (request: PaginateRequest): Promise<PaginateResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl) throw new Error('API URL is not configured');

  const response = await authFetch(`${apiUrl}/v1/query-generator/paginate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'More results'));
  }

  return response.json();
};

/**
 * Create WebSocket connection for batch query real-time updates
 */
export const connectBatchWebSocket = (
  batchId: string,
  onMessage: (data: any) => void,
  onError?: (error: Event) => void,
  onClose?: () => void
): WebSocket => {
  const apiUrl = getApiBaseUrl();
  const wsUrl = apiUrl.replace('http://', 'ws://').replace('https://', 'wss://');

  const ws = new WebSocket(`${wsUrl}/v1/query-generator/ws/batch/${batchId}`);

  ws.onopen = () => {
    console.log('[WebSocket] Connected to batch:', batchId);
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage(data);
    } catch (error) {
      console.error('[WebSocket] Failed to parse message:', error);
    }
  };

  ws.onerror = (error) => {
    console.error('[WebSocket] Error:', error);
    if (onError) onError(error);
  };

  ws.onclose = () => {
    console.log('[WebSocket] Connection closed');
    if (onClose) onClose();
  };

  return ws;
};

/**
 * Cancel all pending/processing queries in a batch.
 */
export const cancelBatchQuery = async (batchId: string): Promise<CancelBatchResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(`${apiUrl}/v1/query-generator/batch/${batchId}/cancel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The request'));
  }

  return response.json();
};

/**
 * Fetch cached query responses from Redis (1h TTL).
 * Called on page load to restore cards after refresh.
 */
export const fetchRecentResponses = async (connectionId: number, collectionId: string): Promise<any[]> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const response = await authFetch(
    `${apiUrl}/v1/query-generator/recent-responses?connection_id=${connectionId}&collection_id=${encodeURIComponent(collectionId)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    }
  );

  if (!response.ok) {
    console.error('[fetchRecentResponses] Failed:', response.status);
    return [];
  }

  const data = await response.json();
  return data.responses || [];
};

/**
 * Remove a specific cached query response from Redis.
 * Called when user clears a card or saves it as a widget.
 */
export const removeRecentResponse = async (connectionId: number, collectionId: string, processId: string): Promise<boolean> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  try {
    const response = await authFetch(
      `${apiUrl}/v1/query-generator/recent-responses/${processId}?connection_id=${connectionId}&collection_id=${encodeURIComponent(collectionId)}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      }
    );

    if (!response.ok) {
      console.error('[removeRecentResponse] Failed:', response.status);
      return false;
    }

    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error('[removeRecentResponse] Error:', error);
    return false;
  }
};
